import { useCallback } from "react";
import type { ReactElement, RefCallback } from "react";
import { Avatar as MeAvatar } from "@kundeninfo/ui/me-design";

/**
 * DER ZUSTÄNDIGE ALS AVATAR (F48, Vorbild GitLab) — dünne Hülle über `me-avatar` (me-design
 * a885596, Entscheid 53; löst Übergang T-5 ab). Das Element zeichnet Kreis, Initialen, die
 * Nutzerfarbe (`nutzer-id` → `farbindex`, FNV-1a mod 8 + 1 — dieselbe Rechnung wie die
 * Diagrammfarben, also stabil je Person), den Rand für „eigen", den gestrichelten Kreis für
 * „nicht zugewiesen", den Steckbrief als Sofort-Popup (Name · Kürzel · Mail) und den
 * zugänglichen Namen (`role="img"`). Hier bleibt nur die Abbildung der Person auf die Attribute.
 *
 * `ohneName` (Akte-Liste): nur der Kreis; dann bekommt er einen eigenen
 * Tabulator-Halt, damit das Popup mit dem Namen auch per Tastatur erreichbar bleibt. Mit
 * sichtbarem Namen kein eigener Halt (Karte, Liste — me-design: keine Halte in Zeilen).
 * Bild-Slot steht; den Profilbild-Upload entscheidet Dirk (137).
 *
 * `ohneTipp`: kein eigenes Sofort-Popup des Kreises. Eine Zelle, die um den Avatar ein eigenes Popup trägt (Teilaufgaben
 * Festlegung 23: Person und Rolle), braucht das — sonst beansprucht der Kreis das Ereignis und das Popup der Zelle
 * schweigt über ihm (`tipp` in basis.js: das innere Element hat Vorrang).
 *
 * `tabhalt`: überschreibt die Vorgabe „eigener Halt nur ohne Namen“ — die Zeilenspalte der Zeitachse zeigt den Kreis ohne
 * Namen in Zeilen, die selbst der Tabulator-Halt sind (wandernder Tabstopp), und braucht dort keinen zweiten.
 */

/** `me-avatar` kennt das Attribut `ohne-tipp`, seine React-Hülle reicht es nicht durch — die Ref setzt es. */
export function useOhneTipp(an: boolean | undefined): RefCallback<HTMLElement> {
  return useCallback((el: HTMLElement | null) => { el?.toggleAttribute("ohne-tipp", an === true); }, [an]);
}

export type AvatarPerson = { id: string; name: string; email?: string | null; bild?: string | null };

export function Avatar(p: {
  person: AvatarPerson | null; ich?: boolean; ohneName?: boolean; ohneTipp?: boolean; tabhalt?: boolean;
}): ReactElement {
  const mitName = p.ohneName !== true;
  const ohneTipp = useOhneTipp(p.ohneTipp);
  const gemeinsam = { mitName, tabhalt: p.tabhalt ?? !mitName, ref: ohneTipp };
  if (p.person === null) return <MeAvatar name="" niemand {...gemeinsam} />;
  const { id, name, email, bild } = p.person;
  return (
    <MeAvatar name={name} nutzerId={id} eigen={p.ich === true} {...gemeinsam}
      {...(email !== undefined && email !== null && email !== "" ? { mail: email } : {})}
      {...(bild !== undefined && bild !== null ? { bild: <img src={bild} alt="" /> } : {})} />
  );
}

/**
 * Die `data-*`-Angaben einer Person für eine `<option>` im `Auswahlmenue` (me-auswahlmenue zeichnet
 * daraus den Avatar je Option, Entscheid 53) — dieselbe Abbildung wie oben, damit Filter, Dialog
 * und Anlegen die Person gleich zeigen (Name, Nutzerfarbe über die Kennung, Mail).
 */
export function personOption(u: AvatarPerson): Record<string, string> {
  return {
    "data-name": u.name, "data-nutzer-id": u.id,
    ...(u.email !== undefined && u.email !== null && u.email !== "" ? { "data-mail": u.email } : {}),
  };
}
